import { CSSProperties } from 'react';

import clsx from 'clsx';
import Image from 'next/image';

import Link from '~/shared/components/Link/Link';
import { useLogoImage } from '~/shared/components/Logo/hooks/useLogoImage';
import { Logo } from '~/shared/components/Logo/Logo';
import { RawHtml } from '~/shared/components/RawHtml/RawHtml';
import { Text } from '~/shared/components/Text/Text';
import { useFrame } from '~/shared/utils/frame/hooks/useFrame';
import { useMarket } from '~/shared/utils/market/hooks/useMarket';

import { FooterNavigationContainer } from './components/FooterNavigationContainer/FooterNavigationContainer';
import { FooterSubnavigation } from './components/FooterSubnavigation/FooterSubnavigation';
import { SocialMediaIcons } from './components/SocialMediaIcons/SocialMediaIcons';
import styles from './n60footer.module.css';

export const N60Footer = () => {
	const {
		data: { footer, market, vertical: { brandNo } = {} },
	} = useFrame();
	const { culture } = useMarket();

	const logo = useLogoImage({
		color: 'light',
		brandNo: brandNo,
		global: true,
	});

	const fallbackLogoImage = (() => {
		if (!logo.image) {
			return undefined;
		}

		const width = logo.image.width ?? 0;
		const height = logo.image.height ?? 0;

		if (width > 0 && height > 0) {
			return logo.image;
		}

		return {
			...logo.image,
			width: width > 0 ? width : 140,
			height: height > 0 ? height : 80,
		};
	})();

	const mobileLogoHeight = (() => {
		const scale = logo.maxHeightScale ?? 0;
		if (scale > 0) {
			return scale * 40;
		}

		return Math.max(fallbackLogoImage?.height ?? 0, 1);
	})();

	const desktopLogoHeight = (() => {
		const scale = logo.maxHeightScaleDesktop ?? 0;
		if (scale > 0) {
			return scale * 110;
		}

		return Math.max(mobileLogoHeight, 1);
	})();

	const footerLogoStyles = {
		'--footer-logo-height': `${mobileLogoHeight}px`,
		'--footer-logo-height-desktop': `${desktopLogoHeight}px`,
	} as CSSProperties;

	if (!footer) {
		return null;
	}

	const { leftHeadline, leftText, centerHeadline, centerMenu, rightHeadline, rightMenu, copyright, paymentIcons, socialMediaIcons } = footer;

	return (
		<footer className={clsx(styles.root, 'global-footer')}>
			<div className={styles.header}>
				<div
					className={styles.logo}
					style={footerLogoStyles}
				>
					<Logo image={fallbackLogoImage} />
				</div>
				<div>
					<div className={styles.bCorpLogo}>
						<Link
							href="https://certifikat.emaerket.dk/rosendahl.com"
							isExternal
						>
							<Image
								src={`/assets/logos-external/e-label.png`}
								width={0}
								height={0}
								style={{ height: '95px', width: 'auto' }}
								alt="ee-trustmark"
							/>
						</Link>
						<Link
							href="https://www.rosendahl.com/en/intl/rosendahldesigngroup/responsibility/b-corp"
							isExternal
							aria-label="RDG B-Corp logo"
						>
							<Image
								src={`/assets/logos-external/b-corp-logo_${culture === 'de' ? culture : 'en'}.png`}
								width={0}
								height={0}
								style={{ height: '95px', width: 'auto' }}
								alt="RDG B-Corp logo"
							/>
						</Link>
					</div>
				</div>
			</div>
			<div className={styles.footerInner}>
				<div className={styles.footerLeft}>
					{leftHeadline ? (
						<Text
							as="h3"
							variant="bodySmBold"
							marginBottom="2"
						>
							{leftHeadline}
						</Text>
					) : null}

					{leftText?.html ? (
						<RawHtml
							className={styles.footerLeftHTML}
							html={leftText?.html}
						/>
					) : null}
					{socialMediaIcons?.length ? (
						<div className={styles.socialIcons}>
							<SocialMediaIcons icons={socialMediaIcons} />
						</div>
					) : null}
				</div>
				<FooterNavigationContainer
					title={centerHeadline}
					navigationItems={centerMenu}
					enableMultiColumns={true}
				></FooterNavigationContainer>

				<FooterNavigationContainer
					hasBorder={true}
					title={rightHeadline}
					navigationItems={rightMenu}
				></FooterNavigationContainer>
			</div>

			<FooterSubnavigation
				copyright={copyright}
				paymentIcons={paymentIcons}
				marketName={market?.name}
			></FooterSubnavigation>
		</footer>
	);
};
